import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { 
  Package, Clock, CheckCircle, Truck, AlertCircle, 
  ArrowLeft, CreditCard, MapPin, Calendar, FileText
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Status configuration
const STATUS_CONFIG = {
  'draft': { 
    label: 'Draft', 
    color: 'bg-gray-100 text-gray-600 border-gray-300 border-dashed',
    dotColor: 'bg-gray-400',
    description: 'Submission not completed'
  },
  'awaiting_shipment': { 
    label: 'Awaiting Shipment', 
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    dotColor: 'bg-blue-500',
    description: 'Please ship your cards to our facility'
  },
  'received': { 
    label: 'Cards Received', 
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    dotColor: 'bg-yellow-500',
    description: 'Your cards have arrived and are in queue'
  },
  'payment_processed': { 
    label: 'Payment Processed', 
    color: 'bg-orange-100 text-orange-800 border-orange-200',
    dotColor: 'bg-orange-500',
    description: 'Payment has been processed successfully'
  },
  'grading_complete': { 
    label: 'Grading Complete', 
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    dotColor: 'bg-purple-500',
    description: 'Your cards have been graded'
  },
  'id_and_encapsulation': { 
    label: 'Encapsulating', 
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    dotColor: 'bg-purple-500',
    description: 'Cards are being encapsulated in holders'
  },
  'shipped': { 
    label: 'Shipped', 
    color: 'bg-green-100 text-green-800 border-green-200',
    dotColor: 'bg-green-500',
    description: 'Your graded cards are on their way!'
  },
  'complete': { 
    label: 'Delivered', 
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    dotColor: 'bg-gray-500',
    description: 'Order complete'
  }
};

const getStatusConfig = (status) => {
  return STATUS_CONFIG[status] || { 
    label: status, 
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    dotColor: 'bg-gray-500',
    description: ''
  };
};

const SubmissionDetailPage = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSubmissionDetail();
  }, [submissionId]);

  const fetchSubmissionDetail = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await axios.get(`${API}/submissions/${submissionId}`);
      setSubmission(response.data);
    } catch (err) {
      console.error('Failed to fetch submission:', err);
      if (err.response?.status === 404) {
        setError('Submission not found');
      } else if (err.response?.status === 403) {
        setError('You do not have permission to view this submission');
      } else {
        setError('Failed to load submission details');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Navbar />
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-[#003594] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-500">Loading submission details...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !submission) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Navbar />
        <main className="flex-grow flex items-center justify-center">
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center max-w-md mx-4">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {error === 'Submission not found' ? 'Submission Not Found' : 'Error'}
            </h2>
            <p className="text-gray-600 mb-6">{error || 'Submission not found'}</p>
            <Button 
              onClick={handleBackToDashboard} 
              className="bg-[#003594] hover:bg-[#002554]"
            >
              Go to Dashboard
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const statusConfig = getStatusConfig(submission.status);

  // Define the customer-facing workflow steps
  const workflowSteps = [
    { 
      id: 'submitted', 
      name: 'Submission Created', 
      description: 'Your order has been submitted',
      statuses: ['awaiting_shipment', 'received', 'payment_processed', 'grading_complete', 'id_and_encapsulation', 'shipped', 'complete'],
      timestamp: submission.created_at
    },
    { 
      id: 'ship', 
      name: 'Ship Your Cards', 
      description: 'ship_step',
      statuses: ['received', 'payment_processed', 'grading_complete', 'id_and_encapsulation', 'shipped', 'complete'],
      timestamp: null
    },
    { 
      id: 'received', 
      name: 'Cards Received', 
      description: 'We have received your cards',
      statuses: ['received', 'payment_processed', 'grading_complete', 'id_and_encapsulation', 'shipped', 'complete'],
      timestamp: submission.received_at
    },
    { 
      id: 'payment', 
      name: 'Payment Processed', 
      description: 'Payment has been collected',
      statuses: ['payment_processed', 'grading_complete', 'id_and_encapsulation', 'shipped', 'complete'],
      timestamp: submission.payment_collected_at
    },
    { 
      id: 'grading', 
      name: 'Grading in Progress', 
      description: 'Your cards are being professionally graded',
      statuses: ['grading_complete', 'id_and_encapsulation', 'shipped', 'complete'],
      timestamp: submission.grading_completed_at
    },
    { 
      id: 'shipped', 
      name: 'Shipped to You', 
      description: 'Your graded cards are on their way',
      statuses: ['shipped', 'complete'],
      timestamp: submission.shipped_at
    },
    { 
      id: 'complete', 
      name: 'Delivered', 
      description: 'Order complete',
      statuses: ['complete'],
      timestamp: submission.completed_at
    }
  ];

  const currentStatus = submission.status;
  const statusToStepIndex = {
    'awaiting_shipment': 1,
    'received': 2,
    'payment_processed': 4,
    'grading_complete': 4,
    'id_and_encapsulation': 4,
    'shipped': 5,
    'complete': 6
  };
  
  const currentStepIndex = statusToStepIndex[currentStatus] ?? 0;
  const isPaymentFailed = submission.payment_failed && currentStatus === 'received';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      
      <main className="flex-grow">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="space-y-6">
            {/* Back Button */}
            <button 
              onClick={handleBackToDashboard}
              data-testid="back-to-dashboard-btn"
              className="flex items-center gap-2 text-[#003594] hover:underline font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </button>

            {/* Header Card */}
            <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="submission-header">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 mb-2" data-testid="submission-number">
                    {submission.submission_number}
                  </h1>
                  <span className={`inline-flex px-4 py-2 rounded-full text-sm font-medium border ${statusConfig.color}`} data-testid="submission-status">
                    {statusConfig.label}
                  </span>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Total</p>
                  <p className="text-3xl font-bold text-[#003594]" data-testid="submission-total">
                    ${(submission.total_price || 0).toFixed(2)}
                  </p>
                  {submission.shipping_cost !== undefined && submission.shipping_cost !== null ? (
                    <p className="text-xs text-gray-400">
                      (Grading: ${((submission.grading_cost) || (submission.total_price - (submission.shipping_cost || 0) - (submission.insurance_cost || 0))).toFixed(2)} + Shipping: ${(submission.shipping_cost).toFixed(2)}
                      {submission.insurance_cost > 0 ? ` + Insurance: $${submission.insurance_cost.toFixed(2)}` : ''})
                    </p>
                  ) : (
                    <p className="text-xs text-gray-400">(grading total)</p>
                  )}
                  {submission.voucher_discount > 0 && (
                    <p className="text-xs text-green-600 mt-1">
                      Discount applied: -${submission.voucher_discount.toFixed(2)}
                    </p>
                  )}
                </div>
              </div>

              {/* Payment Status Banner */}
              {submission.status === 'received' && (
                <div className={`rounded-lg p-4 mb-4 ${
                  submission.payment_failed 
                    ? 'bg-amber-50 border border-amber-200' 
                    : 'bg-blue-50 border border-blue-200'
                }`}>
                  <div className="flex items-start gap-3">
                    {submission.payment_failed ? (
                      <>
                        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-amber-800">Payment Processing</p>
                          <p className="text-sm text-amber-700 mt-1">
                            We&apos;re working to process your payment. If there are any issues with your payment method, 
                            we&apos;ll reach out to you directly. You can also update your payment information in your account settings.
                          </p>
                        </div>
                      </>
                    ) : (
                      <>
                        <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-blue-800">Payment Pending</p>
                          <p className="text-sm text-blue-700 mt-1">
                            Your cards have been received. Payment will be processed shortly.
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Submission Info Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Cards</p>
                  <p className="font-semibold text-gray-900" data-testid="card-count">{submission.card_count}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Price per Card</p>
                  <p className="font-semibold text-gray-900">${submission.price_per_card?.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Pricing Tier</p>
                  <p className="font-semibold text-gray-900">{submission.pricing_tier}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Created</p>
                  <p className="font-semibold text-gray-900">
                    {new Date(submission.created_at).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric', 
                      year: 'numeric' 
                    })}
                  </p>
                </div>
              </div>
            </div>

            {/* Status Timeline */}
            <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="order-progress">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#003594]" />
                  Order Progress
                </h3>
                
                {/* Packing Slip */}
                {(submission.status === 'awaiting_shipment' || submission.status === 'draft') && (
                  <div className="bg-[#003594] rounded-lg px-4 py-3 text-white">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="flex items-center gap-2 flex-1">
                        <FileText className="w-5 h-5 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-sm">Packing Slip</p>
                          <p className="text-xs text-blue-100">Print and include with your cards for faster processing</p>
                        </div>
                      </div>
                      <Link 
                        to={`/submissions/${submission.id}/print`} 
                        target="_blank"
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-semibold text-[#003594] hover:bg-gray-100 transition-colors"
                        data-testid="packing-slip-link"
                      >
                        <FileText className="w-4 h-4" />
                        View / Print
                      </Link>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Timeline Steps */}
              <div className="space-y-0">
                {workflowSteps.map((step, index) => {
                  const isCompleted = step.statuses.includes(currentStatus) && index < currentStepIndex;
                  const isCurrent = index === currentStepIndex;
                  const isPending = index > currentStepIndex;
                  
                  const isShipStep = step.id === 'ship';
                  const shipStepCompleted = isShipStep && ['received', 'payment_processed', 'grading_complete', 'id_and_encapsulation', 'shipped', 'complete'].includes(currentStatus);
                  const showPaymentProcessing = step.id === 'received' && isPaymentFailed;
                  
                  const stepCompleted = isCompleted || (index === 0) || shipStepCompleted;
                  const stepCurrent = isCurrent && !stepCompleted;
                  
                  return (
                    <div key={step.id} className="flex items-start gap-4">
                      <div className="relative flex flex-col items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium z-10 ${
                          stepCompleted 
                            ? 'bg-green-500 text-white' 
                            : stepCurrent
                              ? 'bg-[#003594] text-white ring-4 ring-[#003594]/20'
                              : 'bg-gray-200 text-gray-500'
                        }`}>
                          {stepCompleted ? (
                            <CheckCircle className="w-5 h-5" />
                          ) : (
                            index + 1
                          )}
                        </div>
                        {index < workflowSteps.length - 1 && (
                          <div className={`w-0.5 h-12 ${
                            stepCompleted ? 'bg-green-500' : 'bg-gray-200'
                          }`}></div>
                        )}
                      </div>
                      
                      <div className={`flex-1 pb-8 ${index === workflowSteps.length - 1 ? 'pb-0' : ''}`}>
                        <p className={`font-medium ${
                          stepCompleted 
                            ? 'text-green-700' 
                            : stepCurrent 
                              ? 'text-[#003594]' 
                              : 'text-gray-400'
                        }`}>
                          {step.name}
                        </p>
                        <p className={`text-sm ${stepCompleted || stepCurrent ? 'text-gray-600' : 'text-gray-400'}`}>
                          {step.description === 'ship_step' ? (
                            <>
                              See our <Link to="/shipping-instructions" className="text-[#003594] hover:underline">shipping guide</Link> for packing tips.
                            </>
                          ) : (
                            step.description
                          )}
                        </p>
                        
                        {stepCompleted && step.timestamp && (
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(step.timestamp).toLocaleString('en-US', {
                              month: 'short', day: 'numeric', year: 'numeric',
                              hour: 'numeric', minute: '2-digit', hour12: true
                            })}
                          </p>
                        )}
                        {stepCurrent && !showPaymentProcessing && (
                          <p className="text-xs text-[#003594] font-medium mt-1">In Progress</p>
                        )}
                        {showPaymentProcessing && step.id === 'received' && (
                          <p className="text-xs text-amber-600 font-medium mt-1">Payment Processing...</p>
                        )}
                        {isPending && !stepCompleted && !stepCurrent && (
                          <p className="text-xs text-gray-400 mt-1">Pending</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Shipping Address */}
            {submission.shipping_address && (
              <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="shipping-address">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#003594]" />
                  Return Shipping Address
                </h3>
                <div className="text-gray-600">
                  <p className="font-medium text-gray-900">{submission.shipping_address.name}</p>
                  <p>{submission.shipping_address.street_address}</p>
                  <p>{submission.shipping_address.city}, {submission.shipping_address.state} {submission.shipping_address.zip_code}</p>
                  {submission.shipping_address.phone && <p>{submission.shipping_address.phone}</p>}
                </div>
              </div>
            )}

            {/* Tracking Number */}
            {submission.tracking_number && (
              <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="tracking-info">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Truck className="w-5 h-5 text-[#003594]" />
                  Tracking Information
                </h3>
                <p className="text-gray-600">
                  Tracking Number: <span className="font-mono font-semibold text-gray-900">{submission.tracking_number}</span>
                </p>
              </div>
            )}

            {/* Card Details */}
            {submission.cards && submission.cards.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="card-details">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#003594]" />
                  Card Details ({submission.cards.length} entered)
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-3 font-medium text-gray-500">#</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-500">Year</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-500">Set/Brand</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-500">Card #</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-500">Player</th>
                      </tr>
                    </thead>
                    <tbody>
                      {submission.cards.map((card, index) => (
                        <tr key={card.id} className="border-b border-gray-100">
                          <td className="py-2 px-3 text-gray-600">{index + 1}</td>
                          <td className="py-2 px-3 text-gray-900">{card.year || '-'}</td>
                          <td className="py-2 px-3 text-gray-900">{card.set_brand || '-'}</td>
                          <td className="py-2 px-3 text-gray-900">{card.card_number || '-'}</td>
                          <td className="py-2 px-3 text-gray-900">{card.player_name || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Payment Information */}
            {submission.stripe_payment_method_id && (
              <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="payment-info">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-[#003594]" />
                  Payment Information
                </h3>
                <div className="flex items-center gap-2 text-gray-600">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Payment method on file</span>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Your card will be charged when we receive your cards for grading.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SubmissionDetailPage;
